import React from "react";
import { cn } from "@/lib/utils";
import { PlatformBadge } from "@/components/badges/PlatformBadge";
import { StatusBadge } from "@/components/badges/StatusBadge";
import { formatTime } from "@/lib/utils";
import { GripVertical, RadioTower } from "lucide-react";

const STATUS_ACCENTS = {
  draft: "rgba(100,116,139,0.55)",
  needs_review: "rgba(217,119,6,0.78)",
  approved: "rgba(22,163,74,0.78)",
  scheduled: "rgba(18, 147, 79,0.82)",
  publishing: "rgba(143, 224, 74,0.82)",
  published: "rgba(22,163,74,0.82)",
  failed: "rgba(220,38,38,0.82)",
};

export function CalendarItem({ item, onClick, dense = false, draggable = false, onDragStart, onDragEnd, className }) {
  const accent = STATUS_ACCENTS[item.status] || STATUS_ACCENTS.draft;
  const context = item.publishingContext;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onClick?.(item)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick?.(item); } }}
      data-testid={`calendar-item-${item.id}`}
      draggable={draggable}
      onDragStart={(e) => {
        if (!draggable) return;
        e.dataTransfer.setData("application/x-calendar-item", item.id);
        e.dataTransfer.effectAllowed = "move";
        e.currentTarget.classList.add("opacity-60");
        onDragStart?.(item, e);
      }}
      onDragEnd={(e) => {
        e.currentTarget.classList.remove("opacity-60");
        onDragEnd?.(item, e);
      }}
      className={cn(
        "group relative w-full overflow-hidden rounded-lg border bg-card text-left text-xs shadow-[0_8px_22px_rgba(18, 22, 20,0.05)] transition hover:-translate-y-0.5 hover:border-[color:var(--accent-blue)]/40 hover:shadow-[0_14px_30px_rgba(18, 22, 20,0.10)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent-cyan)]",
        dense ? "px-2 py-1.5" : "px-3 py-2.5",
        draggable && "cursor-grab active:cursor-grabbing",
        className,
      )}
      style={{ borderColor: "rgba(226,232,240,0.95)" }}
    >
      <span aria-hidden className="absolute inset-y-2 left-0 w-1 rounded-r-full" style={{ backgroundColor: accent }} />
      <div className="flex items-center gap-1.5 pl-1">
        {draggable && <GripVertical className="h-3 w-3 text-muted-foreground/50 group-hover:text-muted-foreground" aria-hidden />}
        <PlatformBadge platform={item.platform} compact />
        <span className="ml-auto text-[11px] font-mono text-muted-foreground">{formatTime(item.scheduled_at)}</span>
      </div>
      <p className={cn("mt-1 truncate pl-1 font-semibold text-foreground", dense ? "text-[11px]" : "text-[13px]")}>{item.title}</p>
      {!dense && (
        <div className="mt-2 flex items-center justify-between gap-2 pl-1">
          <StatusBadge status={item.status} size="sm" />
          {context ? (
            <span className="inline-flex min-w-0 items-center gap-1 text-[11px] text-muted-foreground">
              <RadioTower className={cn("h-3 w-3 shrink-0", context.connected ? "text-[color:var(--accent-green)]" : "text-muted-foreground")} />
              <span className="truncate">{context.modeLabel}</span>
            </span>
          ) : null}
        </div>
      )}
    </div>
  );
}
